import {
  Component,
  ChangeDetectionStrategy,
  inject,
  effect,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  SharelockAnimations,
  AnimationStateE,
} from '../../../../../share/utils/animations';
import { SharelockCommonModule } from '../../../../../share/modules/sharelock-common.module';
import {
  INavigationDataTreeUnit,
  ENavigationUnitComponentSelection,
} from '../../../../model/navigation.model';
import { Router } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { IconComponent } from '../../../../../share/components/ui/icon/icon.component';
import { AnimationEvent } from '@angular/animations';
import { NavigationButtonsBase } from '../../components/navigation-buttons/navigation-buttons.clsss';
import { TranslateService } from '@ngx-translate/core';
import { getLanguageAwareExternalUrl } from '../../../../../share/utils/external-url.utils';

@Component({
  selector: 'shrl-sidenav-navigation-buttons',
  imports: [
    SharelockCommonModule,
    MatIconModule,
    IconComponent,
    MatRippleModule,
    MatIconModule,
  ],
  templateUrl: './sidenav-navigation-buttons.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [SharelockAnimations.appear, SharelockAnimations.transformX],
})
export class SidenavNavigationButtonsComponent extends NavigationButtonsBase {
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);

  readonly animationState = signal<AnimationStateE>(AnimationStateE.SHOW);

  readonly displayedUnits = signal<INavigationDataTreeUnit[]>([]);
  readonly parent = signal<INavigationDataTreeUnit | undefined>(undefined);
  readonly parents = signal<INavigationDataTreeUnit[]>([]);
  readonly #selectedUnits = signal<INavigationDataTreeUnit[]>([]);

  constructor() {
    super(ENavigationUnitComponentSelection.SIDENAV);
    this.init();
    this.#setNavigationServiceSubscribers();
  }

  onAnimationDone(event: AnimationEvent) {
    if (event.toState === AnimationStateE.SHOW) {
      return;
    }
    this.#setDisplayTree();
  }

  // ON CLICK ACTIONS
  onNavigationLabelClick(navigationUnit: INavigationDataTreeUnit) {
    if (navigationUnit.isRedirected) {
      this.onNavigationArrowClick(navigationUnit);
    } else if (navigationUnit?.externalUrl) {
      const languageAwareUrl = getLanguageAwareExternalUrl(
        navigationUnit.externalUrl,
        this.#translate
      );
      window.location.href = languageAwareUrl;
    } else if (navigationUnit?.fullPath) {
      this.#router.navigateByUrl(navigationUnit.fullPath);
    }
  }

  onNavigationArrowClick(navigationUnit: INavigationDataTreeUnit) {
    if (navigationUnit?.navigationChildren?.length) {
      this.animationState.set(AnimationStateE.HIDE);
      this.#selectedUnits.set(navigationUnit.navigationChildren);
    } else {
      this.onNavigationLabelClick(navigationUnit);
    }
  }

  // SETTERS
  selectParents() {
    this.animationState.set(AnimationStateE.HIDE);
    this.#selectedUnits.set(this.parents());
  }

  #setDisplayTree() {
    this.displayedUnits.set(this.#selectedUnits());
    this.parent.set(
      this.#getParent(this.navigationUnits(), this.displayedUnits().at(0))
    );
    this.parents.set(
      this.#getLeafs(this.parent(), this.navigationUnits()) ?? []
    );
    this.animationState.set(AnimationStateE.SHOW);
  }

  #setSelectedUnits = (navigationUnits: INavigationDataTreeUnit[]) => {
    const currentUnits = this.#getActiveNavigationUnits(navigationUnits);
    if (currentUnits.length) {
      this.#selectedUnits.set(currentUnits);
    } else {
      this.#selectedUnits.set(navigationUnits);
    }
  };

  #resetNavigationUnits = () => {
    this.#setSelectedUnits(this.navigationUnits());
    this.#setDisplayTree();
  };

  #setNavigationServiceSubscribers() {
    effect(() => {
      if (!this.navigationService.sidenavOpened()) {
        this.#resetNavigationUnits();
      }
    });
  }

  override onNavigationUnitsChange(filteredUnits: INavigationDataTreeUnit[]) {
    super.onNavigationUnitsChange(filteredUnits);
    this.#setSelectedUnits(filteredUnits);
    this.#setDisplayTree();
  }

  // UTILS
  #getActiveNavigationUnits(
    units: INavigationDataTreeUnit[]
  ): INavigationDataTreeUnit[] {
    const isActiveInCurrentLevel = units.some((unit) => unit.matchFullPath);

    if (isActiveInCurrentLevel) {
      return units;
    }

    let activeUnits: INavigationDataTreeUnit[] = [];
    units.forEach((unit) => {
      if (unit.navigationChildren && unit.navigationChildren.length > 0) {
        const childActiveUnits = this.#getActiveNavigationUnits(
          unit.navigationChildren
        );
        activeUnits = activeUnits.concat(childActiveUnits);
      }
    });

    return activeUnits;
  }

  #getLeafs(
    leaf?: INavigationDataTreeUnit,
    units?: INavigationDataTreeUnit[]
  ): INavigationDataTreeUnit[] | undefined {
    if (!units || !leaf) {
      return undefined;
    }

    if (units.some((unit) => unit === leaf)) {
      return units;
    }

    let foundLeafs: INavigationDataTreeUnit[] = [];
    units.forEach((unit) => {
      const leafs = this.#getLeafs(leaf, unit?.navigationChildren);
      if (leafs) {
        foundLeafs = foundLeafs.concat(leafs);
      }
    });
    return foundLeafs.length > 0 ? foundLeafs : undefined;
  }

  #getParent(
    roots?: INavigationDataTreeUnit[],
    child?: INavigationDataTreeUnit
  ): INavigationDataTreeUnit | undefined {
    if (!roots || roots.length === 0 || !child) {
      return undefined;
    }

    for (const root of roots) {
      if (root.navigationChildren && root.navigationChildren.includes(child)) {
        return root;
      }
      const parent = this.#getParent(root.navigationChildren || [], child);
      if (parent) {
        return parent;
      }
    }
    return undefined;
  }
}
